import React, { useState, useEffect, useRef } from 'react';
import usePageTitle from '@/hooks/usePageTitle';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Lock, Maximize, Minimize, Search, RefreshCw, Trash2, Pencil, ChevronLeft, ChevronRight, X, Download } from 'lucide-react';
import FullscreenButton from '@/components/FullscreenButton';
import { jsPDF } from 'jspdf';
import { formatUKTime, formatUKDateTime } from '@/utils/ukTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PIN = '2507';
const SCORES_PER_PAGE = 25;
const REFRESH_INTERVAL = 5000; // 5 seconds

export default function LiveScoresPage() {
  // Page title
  usePageTitle('Live Scores');
  
  // Authentication
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  
  // Competition selection
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState('');
  
  // Scores data
  const [scores, setScores] = useState([]);
  const [filteredScores, setFilteredScores] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Search/Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('all');
  
  // Edit modal
  const [editingScore, setEditingScore] = useState(null);
  const [editFormData, setEditFormData] = useState({
    difficulty: 0,
    execution: 0,
    bonus: 0,
    neutralDeductions: 0
  });
  
  // UI state
  const [isFullScreen, setIsFullScreen] = useState(false);
  const refreshIntervalRef = useRef(null);

  // Team rankings tab
  const [viewMode, setViewMode] = useState('scores'); // 'scores' | 'teams'
  const [teamRankings, setTeamRankings] = useState(null);
  const [teamEventEnabled, setTeamEventEnabled] = useState(false);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportRound, setExportRound] = useState('');
  const [exportCategory, setExportCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [exportingFull, setExportingFull] = useState(false);
  const [showFullExportModal, setShowFullExportModal] = useState(false);
  const WAG_APP_OPTS = [
    { key: 'vault', label: 'VT' },
    { key: 'bars', label: 'UB' },
    { key: 'beam', label: 'BB' },
    { key: 'floor', label: 'FX' },
    { key: 'range', label: 'R&C' },
  ];
  const MAG_APP_OPTS = [
    { key: 'floor', label: 'FX' },
    { key: 'pommel', label: 'PH' },
    { key: 'rings', label: 'SR' },
    { key: 'vault', label: 'VT' },
    { key: 'pbars', label: 'PB' },
    { key: 'hbar', label: 'HB' },
  ];
  const [wagAppSel, setWagAppSel] = useState(['vault', 'bars', 'beam', 'floor']);
  const [magAppSel, setMagAppSel] = useState(['floor', 'pommel', 'rings', 'vault', 'pbars', 'hbar']);

  // PIN authentication
  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinInput === PIN) {
      setIsAuthenticated(true);
      toast.success('Access granted');
    } else {
      toast.error('Incorrect PIN');
      setPinInput('');
    }
  };

  // Load competitions on auth
  useEffect(() => {
    if (isAuthenticated) {
      loadCompetitions();
    }
  }, [isAuthenticated]);

  // Auto-refresh scores when competition selected
  useEffect(() => {
    if (isAuthenticated && selectedCompetition) {
      // Load immediately
      loadScores();
      
      // Set up interval
      refreshIntervalRef.current = setInterval(() => {
        loadScores();
      }, REFRESH_INTERVAL);
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [isAuthenticated, selectedCompetition]);

  // Filter scores when search changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredScores(scores);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = scores.filter(score => {
        if (searchType === 'club') {
          return score.clubName?.toLowerCase().includes(query);
        } else if (searchType === 'gymnast') {
          return score.gymnastName?.toLowerCase().includes(query);
        } else if (searchType === 'number') {
          return score.gymnastNumber?.toString().includes(query);
        } else {
          return (
            score.clubName?.toLowerCase().includes(query) ||
            score.gymnastName?.toLowerCase().includes(query) ||
            score.gymnastNumber?.toString().includes(query) ||
            score.apparatus?.toLowerCase().includes(query)
          );
        }
      });
      setFilteredScores(filtered);
    }
  }, [scores, searchQuery, searchType]);

  // Update pagination
  useEffect(() => {
    const pages = Math.ceil(filteredScores.length / SCORES_PER_PAGE) || 1;
    setTotalPages(pages);
    if (currentPage > pages) {
      setCurrentPage(1);
    }
  }, [filteredScores, currentPage]);

  const loadCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/public/competitions`);
      const comps = response.data.competitions || response.data || [];
      setCompetitions(Array.isArray(comps) ? comps : []);
    } catch (error) {
      console.error('Failed to load competitions:', error);
      toast.error('Failed to load competitions');
    }
  };

  // Load categories and rounds for export
  const loadExportData = async () => {
    if (!selectedCompetition) return;
    
    try {
      // Load categories
      const catRes = await axios.get(`${API}/public/categories/${selectedCompetition}`);
      setCategories(catRes.data.categories || []);
      
      // Load programme for rounds
      const progRes = await axios.get(`${API}/music/programme/${selectedCompetition}`);
      const prog = progRes.data.programme || {};
      const roundsList = (prog.rounds || []).map(r => ({
        number: r.roundNumber,
        name: r.roundName || `Round ${r.roundNumber}`
      }));
      setRounds(roundsList);
    } catch (error) {
      console.error('Failed to load export data:', error);
    }
  };

  // Handle export
  const handleExport = async () => {
    if (!exportCategory) {
      toast.error('Please select a category');
      return;
    }
    
    if (!selectedCompetition) {
      toast.error('Please select a competition first');
      return;
    }
    
    setExporting(true);
    
    try {
      console.log('[Export] Starting export for:', {
        competition: selectedCompetition,
        category: exportCategory,
        round: exportRound
      });
      
      const response = await axios.get(`${API}/export/rankings/${selectedCompetition}`, {
        params: {
          category: exportCategory,
          round: exportRound && exportRound !== 'all' ? exportRound : undefined
        }
      });
      
      console.log('[Export] Data received:', response.data);
      
      if (!response.data || !response.data.rankings) {
        throw new Error('Invalid response from server');
      }
      
      const data = response.data;
      await generatePDF(data);
      setShowExportModal(false);
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('[Export] Failed:', error);
      
      let errorMessage = 'Failed to export rankings';
      if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.detail || error.response.statusText || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  // Generate PDF with jsPDF
  const generatePDF = async (data) => {
    console.log('[PDF] Starting PDF generation');
    console.log('[PDF] Discipline:', data.discipline, 'SheetsDiscipline:', data.sheetsDiscipline);
    
    // A5 dimensions in mm: 148 x 210
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a5'
    });
    
    const pageWidth = 148;
    const pageHeight = 210;
    const margin = 10;
    
    // Apparatus names and lists based on discipline
    const wagApparatusNames = {
      vault: 'VAULT',
      bars: 'BARS',
      beam: 'BEAM',
      floor: 'FLOOR',
      range: 'RANGE & CONDITIONING',
      aa: 'AA - ALL AROUND'
    };
    
    const magApparatusNames = {
      floor: 'FLOOR',
      pommel: 'POMMEL HORSE',
      rings: 'RINGS',
      vault: 'VAULT',
      pbars: 'PARALLEL BARS',
      hbar: 'HIGH BAR',
      aa: 'AA - ALL AROUND'
    };
    
    // Determine which apparatus to show
    let apparatusList = [];
    let apparatusNames = {};
    const discipline = data.discipline || 'WAG';
    
    if (discipline === 'WAG') {
      apparatusList = ['vault', 'bars', 'beam', 'floor', 'range', 'aa'];
      apparatusNames = wagApparatusNames;
    } else if (discipline === 'MAG') {
      apparatusList = ['floor', 'pommel', 'rings', 'vault', 'pbars', 'hbar', 'aa'];
      apparatusNames = magApparatusNames;
    } else {
      // BOTH - shouldn't happen with category-specific exports, but handle it
      apparatusList = ['vault', 'bars', 'beam', 'floor', 'range', 'aa'];
      apparatusNames = wagApparatusNames;
    }
    
    // Load logo image - with better error handling
    let logoImg = null;
    try {
      const logoResponse = await fetch('/scorz-logo.png');
      if (logoResponse.ok) {
        const logoBlob = await logoResponse.blob();
        logoImg = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('Failed to read logo'));
          reader.readAsDataURL(logoBlob);
        });
        console.log('[PDF] Logo loaded successfully');
      }
    } catch (e) {
      console.warn('[PDF] Failed to load logo, using text fallback:', e);
    }
    
    // ========== PAGE 1: FRONT COVER ==========
    let y = 40; // Start lower for cover page
    
    // Large centered logo
    if (logoImg) {
      try {
        // Logo centered, large - 80mm wide, ~20mm tall (4:1 aspect ratio)
        const logoWidth = 80;
        const logoHeight = 20;
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoImg, 'PNG', logoX, y, logoWidth, logoHeight);
      } catch (imgError) {
        console.warn('[PDF] Failed to add cover logo:', imgError);
        pdf.setFontSize(36);
        pdf.setFont('helvetica', 'bold');
        pdf.text('SCORZ', pageWidth / 2, y + 15, { align: 'center' });
      }
    } else {
      pdf.setFontSize(36);
      pdf.setFont('helvetica', 'bold');
      pdf.text('SCORZ', pageWidth / 2, y + 15, { align: 'center' });
    }
    
    y += 50;
    
    // Competition name - large, centered
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    const compTitle = data.competitionTitle || 'Competition';
    
    // Word wrap for long competition names
    const maxWidth = pageWidth - (margin * 2);
    const splitTitle = pdf.splitTextToSize(compTitle, maxWidth);
    pdf.text(splitTitle, pageWidth / 2, y, { align: 'center' });
    
    y += (splitTitle.length * 10) + 15;
    
    // Category - medium, centered
    pdf.setFontSize(20);
    pdf.setFont('helvetica', 'normal');
    const categoryText = data.category || '';
    pdf.text(categoryText, pageWidth / 2, y, { align: 'center' });
    
    y += 15;
    
    // Round info (if specified)
    const roundText = exportRound && exportRound !== 'all' ? `Round ${exportRound}` : 'All Rounds';
    pdf.setFontSize(14);
    pdf.setTextColor(100);
    pdf.text(roundText, pageWidth / 2, y, { align: 'center' });
    
    y += 10;
    
    // Discipline indicator (WAG/MAG)
    pdf.setFontSize(12);
    pdf.setTextColor(0, 100, 200);  // Blue color for discipline
    const disciplineLabel = discipline === 'WAG' ? 'Women\'s Artistic' : discipline === 'MAG' ? 'Men\'s Artistic' : '';
    if (disciplineLabel) {
      pdf.text(disciplineLabel, pageWidth / 2, y, { align: 'center' });
    }
    pdf.setTextColor(0);
    
    // Footer with "Results" text
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text('RESULTS', pageWidth / 2, pageHeight - 30, { align: 'center' });
    
    // ========== APPARATUS RANKING PAGES ==========
    apparatusList.forEach((apparatus, index) => {
      pdf.addPage();
      
      let y = margin;
      
      // Header section
      // Logo (left side) - aspect ratio is ~4:1, so use 32x8mm to maintain proportions
      if (logoImg) {
        try {
          pdf.addImage(logoImg, 'PNG', margin, y, 32, 8);
        } catch (imgError) {
          console.warn('[PDF] Failed to add logo image:', imgError);
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.text('SCORZ', margin, y + 6);
        }
      } else {
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('SCORZ', margin, y + 6);
      }
      
      // Competition info (right side)
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(compTitle, pageWidth - margin, y + 2, { align: 'right' });
      pdf.text(roundText, pageWidth - margin, y + 6, { align: 'right' });
      pdf.text(categoryText, pageWidth - margin, y + 10, { align: 'right' });
      
      y += 12;
      
      // Dotted line
      pdf.setLineDash([1, 1]);
      pdf.setDrawColor(150);
      pdf.line(margin, y, pageWidth - margin, y);
      pdf.setLineDash([]);
      
      y += 8;
      
      // Apparatus name (large, centered) with discipline label
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      const apparatusTitle = apparatusNames[apparatus];
      pdf.text(apparatusTitle, pageWidth / 2, y, { align: 'center' });
      
      y += 8;
      
      // Add discipline label for clarity (smaller, centered below apparatus name)
      if (apparatus !== 'aa' && discipline) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100);
        const discLabel = discipline === 'WAG' ? '(Women\'s)' : discipline === 'MAG' ? '(Men\'s)' : '';
        pdf.text(discLabel, pageWidth / 2, y, { align: 'center' });
        pdf.setTextColor(0);
        y += 4;
      }
      
      y += 4;
      
      // Rankings
      const rankings = data.rankings?.[apparatus] || [];
      
      if (rankings.length === 0) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'normal');
        pdf.text('No results yet', pageWidth / 2, y + 10, { align: 'center' });
      } else {
        // 2x bigger rankings - increased line height and font sizes
        const lineHeight = 18;  // Was 12, now 18 for 2x bigger
        
        rankings.forEach((r) => {
          if (y + lineHeight > pageHeight - margin) return; // Don't overflow page
          
          const rankText = getRankText(r.rank);
          const nameText = r.name || 'Unknown';
          const clubText = r.club || '';
          
          // Handle score - might be number or string
          let scoreText = '';
          if (r.score !== null && r.score !== undefined && r.score !== '') {
            const scoreNum = parseFloat(r.score);
            if (!isNaN(scoreNum)) {
              scoreText = scoreNum.toFixed(3);
            }
          }
          
          // Rank (bold, size 20 - was 12)
          pdf.setFontSize(20);
          pdf.setFont('helvetica', 'bold');
          pdf.text(rankText, margin, y);
          
          // Name (bold, size 20 - was 12)
          pdf.setFontSize(20);
          pdf.setFont('helvetica', 'bold');
          pdf.text(nameText, margin + 22, y);
          
          // Score (right aligned, bold, size 20 - was 12)
          if (scoreText) {
            pdf.setFontSize(20);
            pdf.setFont('helvetica', 'bold');
            pdf.text(scoreText, pageWidth - margin, y, { align: 'right' });
          }
          
          // Club on second line (size 12 - was 8)
          if (clubText) {
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100);
            pdf.text(clubText, margin + 22, y + 6);
            pdf.setTextColor(0);
          }
          
          y += lineHeight;
        });
      }
    });
    
    // Save PDF
    const filename = `${(data.category || 'Rankings').replace(/[^a-z0-9]/gi, '_')}_Rankings.pdf`;
    pdf.save(filename);
    console.log('[PDF] Saved as:', filename);
  };

  // Convert rank number to ordinal text
  const getRankText = (rank) => {
    if (rank === 1) return '1st';
    if (rank === 2) return '2nd';
    if (rank === 3) return '3rd';
    return `${rank}th`;
  };

  // Export the FULL results PDF (every gymnast, every category, colour-coded ranks).
  // Opens a small dialog first so the user can pick which apparatus to include
  // for WAG vs MAG categories (so MAG-* rows aren't squeezed into WAG headers).
  const openFullExportModal = () => {
    if (!selectedCompetition) {
      toast.error('Please select a competition first');
      return;
    }
    setShowFullExportModal(true);
  };

  const handleExportFullResults = async () => {
    if (!selectedCompetition) {
      toast.error('Please select a competition first');
      return;
    }
    if (wagAppSel.length === 0 && magAppSel.length === 0) {
      toast.error('Pick at least one apparatus');
      return;
    }
    setExportingFull(true);
    try {
      const params = new URLSearchParams();
      if (wagAppSel.length) params.set('wag_apparatus', wagAppSel.join(','));
      if (magAppSel.length) params.set('mag_apparatus', magAppSel.join(','));
      const response = await axios.get(
        `${API}/export/full-results/${selectedCompetition}?${params.toString()}`,
        { responseType: 'blob' }
      );

      // Pull filename from Content-Disposition if present
      let filename = 'Full_Results.pdf';
      const dispo = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      if (dispo) {
        const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo);
        if (match && match[1]) filename = decodeURIComponent(match[1]);
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Full results PDF exported');
      setShowFullExportModal(false);
    } catch (error) {
      console.error('[Export Full] Failed:', error);
      let msg = 'Failed to export full results';
      if (error.response && error.response.data) {
        // Blob error → read text
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch (_) {
          msg = error.response.statusText || msg;
        }
      }
      toast.error(msg);
    } finally {
      setExportingFull(false);
    }
  };

  const loadScores = async () => {
    if (!selectedCompetition) return;
    
    try {
      const response = await axios.get(`${API}/livescores/${selectedCompetition}`);
      const newScores = response.data.scores || [];
      // Sort by timestamp descending (newest first)
      newScores.sort((a, b) => new Date(b.createdAt || b.submittedAt || 0) - new Date(a.createdAt || a.submittedAt || 0));
      setScores(newScores);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load scores:', error);
    }
  };

  const loadTeamRankings = async () => {
    if (!selectedCompetition) return;
    try {
      const response = await axios.get(`${API}/livescores/${selectedCompetition}/teams`);
      setTeamRankings(response.data);
      setTeamEventEnabled((response.data?.apparatusList || []).length > 0 || (response.data?.teams || []).length > 0 || !!response.data?.teamCountingScores);
    } catch (error) {
      console.error('Failed to load team rankings:', error);
      setTeamRankings({ teams: [], apparatusList: [], teamCountingScores: 0 });
    }
  };

  // Detect whether selected competition has team events enabled so the tab
  // is hidden when teams are off (server returns empty payload either way).
  useEffect(() => {
    if (!selectedCompetition) {
      setTeamEventEnabled(false);
      return;
    }
    const comp = competitions.find(c => c.id === selectedCompetition);
    setTeamEventEnabled(!!comp?.teamEventEnabled);
  }, [selectedCompetition, competitions]);

  // When viewMode flips to 'teams' or comp changes, refresh team data + poll every 15s.
  useEffect(() => {
    if (!selectedCompetition || viewMode !== 'teams') return;
    loadTeamRankings();
    const id = setInterval(loadTeamRankings, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [selectedCompetition, viewMode]);

  const handleDeleteScore = async (scoreId) => {
    if (!confirm('Are you sure you want to delete this score? The judge will need to re-enter it.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/livescores/score/${scoreId}`);
      toast.success('Score deleted');
      loadScores();
    } catch (error) {
      console.error('Failed to delete score:', error);
      toast.error('Failed to delete score');
    }
  };

  const handleEditScore = (score) => {
    setEditingScore(score);
    setEditFormData({
      difficulty: score.difficulty || 0,
      execution: score.execution || 0,
      bonus: score.bonus || 0,
      neutralDeductions: score.neutralDeductions || 0
    });
  };

  const handleSaveEdit = async () => {
    if (!editingScore) return;
    
    try {
      await axios.put(`${API}/livescores/score/${editingScore.id}`, editFormData);
      toast.success('Score updated');
      setEditingScore(null);
      loadScores();
    } catch (error) {
      console.error('Failed to update score:', error);
      toast.error('Failed to update score');
    }
  };

  const calculateFinalScore = () => {
    const d = parseFloat(editFormData.difficulty) || 0;
    const e = parseFloat(editFormData.execution) || 0;
    const b = parseFloat(editFormData.bonus) || 0;
    const n = parseFloat(editFormData.neutralDeductions) || 0;
    return (10.0 + d - e + b - n).toFixed(3);
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullScreen(true);
    } else {
      document.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return formatUKTime(dateString, true);
  };

  const getApparatusColor = (apparatus) => {
    const colors = {
      vault: 'bg-purple-100 text-purple-800',
      bars: 'bg-blue-100 text-blue-800',
      beam: 'bg-pink-100 text-pink-800',
      floor: 'bg-green-100 text-green-800',
      range: 'bg-orange-100 text-orange-800',
      range: 'bg-yellow-100 text-yellow-800'
    };
    return colors[apparatus?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  // Get current page scores
  const startIndex = (currentPage - 1) * SCORES_PER_PAGE;
  const currentScores = filteredScores.slice(startIndex, startIndex + SCORES_PER_PAGE);

  // PIN Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Lock className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <CardTitle className="text-2xl">Live Scores</CardTitle>
            <p className="text-gray-600">Enter PIN to access</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter 4-digit PIN"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                className="text-center text-2xl tracking-widest"
                maxLength={4}
                autoFocus
              />
              <Button type="submit" className="w-full bg-black hover:bg-gray-800">
                Access Live Scores
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src="/scorz-logo.png" alt="Scorz" className="h-10" />
              <div>
                <h1 className="text-xl font-bold">Live Scores</h1>
                {lastRefresh && (
                  <p className="text-xs text-gray-500">
                    Last refresh: {formatTime(lastRefresh.toISOString())} • Auto-refresh every 5s
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Competition Selector */}
              <Select value={selectedCompetition} onValueChange={setSelectedCompetition}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select competition..." />
                </SelectTrigger>
                <SelectContent>
                  {competitions.map(comp => (
                    <SelectItem key={comp.id} value={comp.id}>
                      {comp.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button onClick={loadScores} variant="outline" size="icon">
                <RefreshCw className="w-4 h-4" />
              </Button>
              
              <Button 
                onClick={() => {
                  loadExportData();
                  setShowExportModal(true);
                }} 
                variant="outline"
                disabled={!selectedCompetition}
                data-testid="export-top6-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>

              <Button
                onClick={openFullExportModal}
                variant="outline"
                disabled={!selectedCompetition || exportingFull}
                className="border-black bg-black text-white hover:bg-gray-800 hover:text-white"
                data-testid="export-full-results-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportingFull ? 'Exporting…' : 'Export Full Results'}
              </Button>
              
              <FullscreenButton />
            </div>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center gap-3 max-w-6xl mx-auto">
          {/* View mode tabs */}
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg">
            <button
              data-testid="tab-scores"
              onClick={() => setViewMode('scores')}
              className={`px-3 py-1.5 text-sm rounded-md transition ${viewMode === 'scores' ? 'bg-black text-white shadow' : 'text-gray-700 hover:bg-gray-200'}`}
            >
              Scores
            </button>
            {teamEventEnabled && (
              <button
                data-testid="tab-teams"
                onClick={() => setViewMode('teams')}
                className={`px-3 py-1.5 text-sm rounded-md transition ${viewMode === 'teams' ? 'bg-black text-white shadow' : 'text-gray-700 hover:bg-gray-200'}`}
              >
                Teams
              </button>
            )}
          </div>

          {viewMode === 'scores' && (
            <>
              <div className="flex-1 flex items-center gap-2">
                <Search className="w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1"
                />
                {searchQuery && (
                  <Button variant="ghost" size="icon" onClick={() => setSearchQuery('')}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Fields</SelectItem>
                  <SelectItem value="club">Club</SelectItem>
                  <SelectItem value="gymnast">Gymnast</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                </SelectContent>
              </Select>
            </>
          )}
          {viewMode === 'teams' && (
            <div className="flex-1 flex justify-end">
              <Button variant="outline" size="sm" onClick={loadTeamRankings} data-testid="refresh-team-rankings">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh teams
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="p-4">
        {!selectedCompetition ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-xl">Select a competition to view live scores</p>
          </div>
        ) : viewMode === 'teams' ? (
          <TeamRankingsPanel data={teamRankings} />
        ) : currentScores.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-xl">
              {searchQuery ? 'No scores match your search' : 'No scores yet'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[repeat(15,minmax(0,1fr))] gap-2 px-4 py-3 bg-gray-50 text-xs text-gray-600 font-medium border-b">
              <div className="col-span-1">Time</div>
              <div className="col-span-1">No.</div>
              <div className="col-span-2">Gymnast</div>
              <div className="col-span-2">Club</div>
              <div className="col-span-1">Apparatus</div>
              <div className="col-span-1 text-right">D</div>
              <div className="col-span-1 text-right">E</div>
              <div className="col-span-1 text-right">B</div>
              <div className="col-span-1 text-right">ND</div>
              <div className="col-span-2 text-right">Final</div>
              <div className="col-span-2 text-center">Actions</div>
            </div>
            
            {/* Score Rows */}
            {currentScores.map((score, index) => (
              <div 
                key={score.id || index}
                className={`grid grid-cols-[repeat(15,minmax(0,1fr))] gap-2 px-4 py-3 items-center border-b last:border-b-0 ${
                  index === 0 ? 'bg-green-50 border-l-4 border-l-green-500' : 'hover:bg-gray-50'
                }`}
              >
                <div className="col-span-1 text-gray-500 text-sm">
                  {formatTime(score.createdAt || score.submittedAt)}
                </div>
                <div className="col-span-1 font-bold text-lg">
                  {score.gymnastNumber || '-'}
                </div>
                <div className="col-span-2 truncate font-medium">
                  {score.gymnastName || 'Unknown'}
                </div>
                <div className="col-span-2 text-gray-600 truncate">
                  {score.clubName || '-'}
                </div>
                <div className="col-span-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getApparatusColor(score.apparatus)}`}>
                    {score.apparatus?.toUpperCase() || '-'}
                  </span>
                </div>
                <div className="col-span-1 text-right font-mono">
                  {parseFloat(score.difficulty || 0).toFixed(1)}
                </div>
                <div className="col-span-1 text-right font-mono">
                  {parseFloat(score.execution || 0).toFixed(1)}
                </div>
                <div className="col-span-1 text-right font-mono text-green-600">
                  {parseFloat(score.bonus || 0).toFixed(1)}
                </div>
                <div className="col-span-1 text-right font-mono text-red-600">
                  {parseFloat(score.neutralDeductions || 0).toFixed(1)}
                </div>
                <div className="col-span-2 text-right font-bold text-lg">
                  {parseFloat(score.finalScore || 0).toFixed(3)}
                </div>
                <div className="col-span-2 flex justify-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handleEditScore(score)}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={() => handleDeleteScore(score.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {viewMode === 'scores' && totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </main>

      {/* Edit Modal */}
      <Dialog open={!!editingScore} onOpenChange={() => setEditingScore(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Score</DialogTitle>
          </DialogHeader>
          
          {editingScore && (
            <div className="space-y-4">
              <div className="bg-gray-100 p-3 rounded">
                <p className="font-bold">{editingScore.gymnastName}</p>
                <p className="text-sm text-gray-600">
                  #{editingScore.gymnastNumber} • {editingScore.clubName} • {editingScore.apparatus?.toUpperCase()}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>D Score (Difficulty)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editFormData.difficulty}
                    onChange={(e) => setEditFormData({...editFormData, difficulty: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>E Score (Execution)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editFormData.execution}
                    onChange={(e) => setEditFormData({...editFormData, execution: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>B Score (Bonus)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editFormData.bonus}
                    onChange={(e) => setEditFormData({...editFormData, bonus: parseFloat(e.target.value) || 0})}
                    data-testid="edit-bonus-input"
                  />
                </div>
                <div>
                  <Label>Neutral Deductions</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editFormData.neutralDeductions}
                    onChange={(e) => setEditFormData({...editFormData, neutralDeductions: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
              
              <div className="bg-black text-white p-4 rounded text-center">
                <p className="text-sm opacity-80">Final Score</p>
                <p className="text-3xl font-bold">{calculateFinalScore()}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingScore(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-green-600 hover:bg-green-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export Rankings PDF</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Round (Optional)</Label>
              <Select value={exportRound} onValueChange={setExportRound}>
                <SelectTrigger>
                  <SelectValue placeholder="All Rounds" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rounds</SelectItem>
                  {rounds.map(r => (
                    <SelectItem key={r.number} value={String(r.number)}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Category *</Label>
              <Select value={exportCategory} onValueChange={setExportCategory}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
              <p>This will generate a PDF with 7 A5 pages:</p>
              <ul className="list-disc ml-4 mt-1">
                <li><strong>Front Cover</strong> - Competition name & category</li>
                <li>Vault</li>
                <li>Bars</li>
                <li>Beam</li>
                <li>Floor</li>
                <li>Range & Conditioning</li>
                <li>AA - All Around</li>
              </ul>
              <p className="mt-2">Each page shows top 6 rankings with scores (including ties)</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleExport} disabled={exporting || !exportCategory}>
              {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full Results — Apparatus selection */}
      <Dialog open={showFullExportModal} onOpenChange={setShowFullExportModal}>
        <DialogContent className="sm:max-w-lg" data-testid="full-export-modal">
          <DialogHeader>
            <DialogTitle>Export Full Results</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Pick which apparatus each discipline competed on. Only the apparatus you tick will appear as headers in the PDF.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="border rounded-lg p-3">
              <p className="text-sm font-semibold mb-2">WAG apparatus</p>
              <div className="flex flex-wrap gap-2">
                {WAG_APP_OPTS.map(opt => {
                  const active = wagAppSel.includes(opt.key);
                  return (
                    <button
                      key={`wag-${opt.key}`}
                      type="button"
                      onClick={() => {
                        setWagAppSel(prev => active ? prev.filter(k => k !== opt.key) : [...prev, opt.key]);
                      }}
                      data-testid={`wag-app-${opt.key}`}
                      className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">Default: VT, UB, BB, FX (4-app round)</p>
            </div>

            <div className="border rounded-lg p-3">
              <p className="text-sm font-semibold mb-2">MAG apparatus</p>
              <div className="flex flex-wrap gap-2">
                {MAG_APP_OPTS.map(opt => {
                  const active = magAppSel.includes(opt.key);
                  return (
                    <button
                      key={`mag-${opt.key}`}
                      type="button"
                      onClick={() => {
                        setMagAppSel(prev => active ? prev.filter(k => k !== opt.key) : [...prev, opt.key]);
                      }}
                      data-testid={`mag-app-${opt.key}`}
                      className={`px-3 py-1 rounded-full text-sm font-medium border transition-colors ${active ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">Default: all 6 apparatus</p>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowFullExportModal(false)} data-testid="full-export-cancel">Cancel</Button>
            <Button
              onClick={handleExportFullResults}
              disabled={exportingFull || (wagAppSel.length === 0 && magAppSel.length === 0)}
              className="bg-black text-white hover:bg-gray-800"
              data-testid="full-export-confirm"
            >
              <Download className="w-4 h-4 mr-2" />
              {exportingFull ? 'Exporting…' : 'Export PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Apparatus label maps used by the team rankings panel.
const APPARATUS_LABELS = {
  vault: 'VT',
  bars: 'UB',
  beam: 'BB',
  floor: 'FX',
  range: 'R&C',
  pommel: 'PH',
  rings: 'SR',
  pbars: 'PB',
  hbar: 'HB',
};

function TeamRankingsPanel({ data }) {
  if (!data) {
    return (
      <div className="text-center py-20" data-testid="team-rankings-loading">
        <p className="text-gray-500 text-xl">Loading team rankings…</p>
      </div>
    );
  }
  const teams = data.teams || [];
  const apparatusList = data.apparatusList || [];
  const counting = data.teamCountingScores || 3;

  if (teams.length === 0) {
    return (
      <div className="text-center py-20" data-testid="team-rankings-empty">
        <p className="text-gray-500 text-xl">No teams have been entered yet.</p>
        <p className="text-gray-400 text-sm mt-2">Teams appear here once clubs enter at least one team for this competition.</p>
      </div>
    );
  }

  const RANK_MEDALS = ['🥇','🥈','🥉'];

  return (
    <div className="space-y-5" data-testid="team-rankings-table">
      {/* Header note */}
      <div className="text-xs text-gray-400 px-1">
        Top <strong>{counting}</strong> scores per apparatus count toward each team total.
        <span className="ml-2 inline-flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-400" />
          = counts
        </span>
      </div>

      {teams.map((team) => {
        // Only show apparatus where this team actually has gymnasts
        const activeApp = apparatusList.filter(app =>
          (team.perApparatus?.[app]?.gymnasts || []).length > 0
        );

        return (
          <div
            key={team.id}
            className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm"
            data-testid={`team-card-${team.id}`}
          >
            {/* Team header */}
            <div className="flex items-center justify-between px-5 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-black text-gray-900 w-8">
                  {RANK_MEDALS[team.rank - 1] || team.rank}
                </span>
                <div>
                  <div className="font-bold text-gray-900 text-base leading-tight">
                    {team.name}
                    {team.discipline && (
                      <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-black text-white font-normal">{team.discipline}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">{team.clubName}{team.category ? ` · ${team.category}` : ''}</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black font-mono text-gray-900">{team.total.toFixed(3)}</div>
                <div className="text-xs text-gray-400">{team.gymnastCount} gymnast{team.gymnastCount !== 1 ? 's' : ''}</div>
              </div>
            </div>

            {/* Per-apparatus columns */}
            {activeApp.length > 0 && (
              <div className="overflow-x-auto">
                <div
                  className="grid divide-x divide-gray-100 min-w-max"
                  style={{ gridTemplateColumns: `repeat(${activeApp.length}, minmax(110px, 1fr))` }}
                >
                  {activeApp.map(app => {
                    const ap = team.perApparatus[app] || { gymnasts: [], subtotal: 0, counted: 0 };
                    return (
                      <div key={app} className="flex flex-col">
                        {/* Apparatus header */}
                        <div className="text-center text-xs font-bold text-gray-500 uppercase tracking-widest px-3 py-2 bg-gray-50 border-b border-gray-100">
                          {APPARATUS_LABELS[app] || app.toUpperCase()}
                        </div>

                        {/* Gymnast score rows */}
                        <div className="flex flex-col gap-0 flex-1">
                          {ap.gymnasts.map((g, idx) => (
                            <div
                              key={g.gymnastId}
                              className={`flex items-center justify-between px-3 py-1.5 border-b border-gray-50 last:border-b-0 ${
                                g.counts ? 'bg-emerald-50' : 'bg-white opacity-60'
                              }`}
                            >
                              <span className={`text-xs truncate max-w-[60px] ${g.counts ? 'text-gray-700' : 'text-gray-400'}`}>
                                {g.number ? `${g.number}` : g.name.split(' ')[0]}
                              </span>
                              <span className={`font-mono text-sm font-bold tabular-nums ${g.counts ? 'text-gray-900' : 'text-gray-400'}`}>
                                {g.score.toFixed(3)}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Apparatus subtotal */}
                        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-right">
                          <div className="font-mono font-bold text-sm text-gray-900">{ap.subtotal.toFixed(3)}</div>
                          <div className="text-[10px] text-gray-400">{ap.counted}/{counting}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
